// ADR-0163 (fase 3, ola 3, #0770) — el jefe del proyecto (o un gerente de
// postventa) convierte una consulta del propietario en observación, con
// una sugerencia de la IA que él confirma o corrige.
//
// Staff (dashboard):
//   POST /owner-inquiries/:id/conversion/suggestion → la IA propone la
//        clasificación a partir del texto del propietario y hasta 10 de sus
//        fotos. NO ESCRIBE NADA salvo la constancia en audit_events (sin
//        texto). Sólo a pedido del jefe; con la sugerencia apagada en el
//        ambiente responde 409 y nada viaja a la IA.
//   POST /owner-inquiries/:id/conversion → crea la observación (canal
//        `consulta_propietario`), deja el mensaje en el hilo, cierra la
//        consulta y avisa al propietario. Requiere Idempotency-Key.
//
// Lo que el jefe manda es la clasificación FINAL (los cinco ejes y las tres
// respuestas de severidad) y la descripción para el contratista, que es lo
// único escrito por una persona que cruza al enlace público. La especialidad
// se deriva de la partida; la severidad, de las tres respuestas; el canal,
// el título y la fecha de la observación los fija el servidor.
import { DERIVED_SEVERITIES } from "@bitacorapro/core-engine";
import { z } from "zod";
import { criticalAlertCreateResultSchema } from "./critical-alerts.js";
import { INTERNAL_CLASSIFY_FINDING_MAX_PHOTOS } from "./internal-classify-finding.js";
import { ownerInquiryEntrySchema, ownerInquiryListItemSchema } from "./owner-inquiries.js";
import { ownerNoticeOutcomeSchema } from "./owner-notifications.js";
/** Fotos del propietario que pueden quedar en la observación (y viajar a la
 *  IA): el mismo tope del clasificador. */
export const OWNER_INQUIRY_CONVERSION_MAX_PHOTOS = INTERNAL_CLASSIFY_FINDING_MAX_PHOTOS;
/** La descripción para el contratista. */
export const CONVERSION_DESCRIPCION_MAX = 4000;
/** La nota opcional al propietario (va al hilo, debajo del texto fijo). */
export const CONVERSION_NOTA_MAX = 1000;
/** Por cuenta y minuto. */
export const OWNER_INQUIRY_CONVERSION_RATE_LIMIT = 10;
export const OWNER_INQUIRY_SUGGESTION_RATE_LIMIT = 10;
/** Códigos de error que el dashboard distingue. */
export const OWNER_INQUIRY_CONVERT_FORBIDDEN = "OWNER_INQUIRY_CONVERT_FORBIDDEN";
export const OWNER_INQUIRY_ALREADY_CONVERTED = "OWNER_INQUIRY_ALREADY_CONVERTED";
export const INQUIRY_AUTHOR_NOT_CURRENT_OWNER = "INQUIRY_AUTHOR_NOT_CURRENT_OWNER";
export const INQUIRY_SUGGESTION_DISABLED = "INQUIRY_SUGGESTION_DISABLED";
export const CONVERSION_ATTACHMENT_INVALID = "CONVERSION_ATTACHMENT_INVALID";
export const CONVERSION_CLASSIFICATION_INCOMPLETE = "CONVERSION_CLASSIFICATION_INCOMPLETE";
export const CONVERSION_SPECIALTY_NOT_ALLOWED = "CONVERSION_SPECIALTY_NOT_ALLOWED";
/** La receta no tiene canal del propietario (facility, residential). */
export const OWNER_INQUIRY_CHANNEL_DISABLED = "OWNER_INQUIRY_CHANNEL_DISABLED";
/** DELETE /findings/:id sobre una observación que nació de una consulta. */
export const FINDING_FROM_OWNER_INQUIRY = "FINDING_FROM_OWNER_INQUIRY";
/** Los ejes que la conversión exige, en el orden de lectura del formulario.
 *  Vocabulario PROPIO (no `CLASSIFICATION_AXIS_CODES`, que tiene tres): la
 *  conversión además exige la partida, «¿hay agua activa?» cuando el tipo
 *  de falla lo declara (alimenta la criticidad) y la severidad. El ancho de
 *  una fisura NO se exige: el catálogo lo pide «si se dispone de galga», y
 *  quien convierte mira las fotos del propietario, no la fisura. */
export const CONVERSION_EJES = [
    "recinto",
    "elemento",
    "tipo_reparacion",
    "tipo_falla",
    "agua_activa",
    "severidad",
];
export const conversionEjeSchema = z.enum(CONVERSION_EJES);
const codigo = z.string().min(1).max(16);
/** POST …/conversion/suggestion. Sin `attachmentIds`, el servidor usa las
 *  10 fotos más recientes del propietario. */
export const ownerInquiryConversionSuggestionRequestSchema = z
    .object({
    attachmentIds: z.array(z.string().uuid()).max(OWNER_INQUIRY_CONVERSION_MAX_PHOTOS).optional(),
})
    .strict();
/** La clasificación sugerida, ya normalizada al canal (recinto privado, sin
 *  partida «Otro», campos adicionales sólo con el tipo de falla que los
 *  declara, P1 forzado por regla). Cualquier eje puede venir null. */
export const ownerInquiryConversionSugerenciaSchema = z.object({
    codRecinto: codigo.nullable(),
    codElemento: codigo.nullable(),
    codPartida: codigo.nullable(),
    codTipoFalla: codigo.nullable(),
    aguaActiva: z.boolean().nullable(),
    anchoMm: z.number().nullable(),
    p1Risk: z.boolean().nullable(),
    p2FunctionOrProgression: z.boolean().nullable(),
    p3MinorFunctional: z.boolean().nullable(),
    derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable(),
});
export const ownerInquiryConversionSuggestionResponseSchema = z.object({
    /** null = la IA no pudo clasificar (el jefe elige a mano). */
    sugerencia: ownerInquiryConversionSugerenciaSchema.nullable(),
    /** El sobre sellado de la propuesta del modelo (opaco: el cliente lo
     *  devuelve tal cual en la conversión). Atado a ESTA consulta. */
    propuesta: z.string().min(1).max(8192).nullable(),
    propuestaExpiraEn: z.string().datetime().nullable(),
    /** Cuántas fotos se le mandaron a la IA. */
    fotosEnviadas: z.number().int().min(0).max(OWNER_INQUIRY_CONVERSION_MAX_PHOTOS),
});
/** POST …/conversion. Los códigos son nullable para que un eje faltante
 *  llegue al 422 `CONVERSION_CLASSIFICATION_INCOMPLETE` (con la lista) y no
 *  a un 400 de forma. No acepta `codEspecialidad` (sale de la partida),
 *  `reportId`, `intakeChannel`, `derivedSeverity` ni confianza. */
export const ownerInquiryConversionRequestSchema = z
    .object({
    codRecinto: codigo.nullable(),
    codElemento: codigo.nullable(),
    codPartida: codigo.nullable(),
    codTipoFalla: codigo.nullable(),
    aguaActiva: z.boolean().nullable().optional(),
    anchoMm: z.number().positive().max(1000).nullable().optional(),
    p1Risk: z.boolean().nullable(),
    p2FunctionOrProgression: z.boolean().nullable(),
    p3MinorFunctional: z.boolean().nullable(),
    /** Lo único escrito por una persona que ve el contratista. */
    descripcion: z.string().trim().min(1).max(CONVERSION_DESCRIPCION_MAX),
    /** Al propietario, en el hilo, debajo del texto fijo. */
    nota: z.string().trim().min(1).max(CONVERSION_NOTA_MAX).optional(),
    attachmentIds: z.array(z.string().uuid()).max(OWNER_INQUIRY_CONVERSION_MAX_PHOTOS).default([]),
    /** El sobre de la sugerencia, si la hubo. */
    propuesta: z.string().min(1).max(8192).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (new Set(v.attachmentIds).size !== v.attachmentIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachmentIds"],
            message: "Hay una foto repetida.",
        });
    }
});
export const ownerInquiryConversionResponseSchema = z.object({
    conversion: z.object({
        id: z.string().uuid(),
        findingId: z.string().uuid(),
        folio: z.string().nullable(),
        convertedAt: z.string().datetime(),
        convertedByUserId: z.string().uuid(),
    }),
    observation: z.object({
        id: z.string().uuid(),
        folio: z.string().nullable(),
        derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable(),
        observationStatus: z.string().nullable(),
    }),
    /** La fila de la bandeja ya con `conversion`. */
    inquiry: ownerInquiryListItemSchema,
    /** El mensaje de la conversión en el hilo. */
    message: ownerInquiryEntrySchema,
    /** El correo «Tu consulta ahora es una observación» salió. */
    ownerNotified: z.boolean(),
    ownerNotice: ownerNoticeOutcomeSchema.optional(),
    /** ADR-0156 — si la observación nació Crítica. */
    criticalAlert: criticalAlertCreateResultSchema.optional(),
    /** true = la misma clave ya había convertido: no se reenvía el correo; el
     *  aviso de Crítica se reintenta (su claim lo deduplica). */
    replayed: z.literal(true).optional(),
});
/** El texto fijo del mensaje de la conversión. Sin folio: el mensaje lo lee
 *  el propietario y la regla 9 del spec (fitness F7) dice que nunca ve
 *  folios; la marca «Pasó a observación» y su enlace lo llevan a la
 *  observación. */
export const OWNER_INQUIRY_CONVERSION_MESSAGE = "Registramos tu consulta como una observación para revisarla.";
/** El texto fijo del mensaje de la conversión, más la nota opcional del jefe
 *  en un párrafo aparte. Neutro a propósito: no nombra a nadie del equipo
 *  ni lleva el folio. Cabe en los 2.000 caracteres del hilo (nota ≤ 1.000). */
export function ownerInquiryConversionMessage(args) {
    const nota = args.nota?.trim();
    return nota ? `${OWNER_INQUIRY_CONVERSION_MESSAGE}\n\n${nota}` : OWNER_INQUIRY_CONVERSION_MESSAGE;
}
//# sourceMappingURL=owner-inquiry-conversion.js.map